<template>
  <div
    id="app"
    class="root"
    :class="{
      'has-cookie-notice': showSetPasswordNotification,
    }">
    <set-password-notification
      v-show="showSetPasswordNotification"
      @set-password="openSetPasswordPopup" />

    <template v-if="isLoaded">
      <cookies-popup
        v-show="shouldShowExtendedCookieNotification"
        :is-e-u="isEU"
        @accept="acceptCookie" />
      <cookies-popup
        v-show="shouldShowCookieNotification"
        :is-e-u="isEU"
        @accept="acceptCookie"
        @close="closeCookie" />
    </template>

    <vuedal
      v-if="vuedalsIsReady"
      ref="vuedals"
      @mouseup.native.self="vuedalsClickType = 'mouseup'"
      @mousedown.native.self="closeVuedal" />
    <screen-holder />
    <router-view />
    <tooltip-layer />
    <notifications v-if="!isPlatformApp" />
    <component :is="SignedAsInfoComponent" />
  </div>
</template>

<script>
/** @see build/webpack.client.config.js:71 */
/* eslint-disable no-unused-vars */
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import BaseTooltip from 'Components/ui/Tooltip/BaseTooltip.vue';
import ErrorTooltip from 'Components/ui/Tooltip/ErrorTooltip.vue';
import UiInput from 'Components/ui/Input/UiInput.vue';
import BaseSelectNew from 'Components/ui/Select/BaseSelectNew.vue';
import UiKitSelect from 'Components/ui/Select/UiKitSelect.vue';
import SearchInput from 'Components/ui/Input/SearchInput.vue';
/* eslint-enable */

import axios from 'axios';
import {
  mapState, mapMutations, mapGetters, mapActions,
} from 'vuex';
import RegionModel from 'Models/RegionModel';
import {
  hasTouch, setWindowFPS, setWindowVisibilityAPI, throttle,
} from 'Root/helpers/tools';
import AppModel from 'Models/AppModel';
import { POPUP_PROMOCODE_APPLIED } from 'Models/PopupModel';
import HttpErrorModel from 'Models/HttpErrorModel';
import TooltipLayer from 'Components/ui/Tooltip/TooltipLayer.vue';
import Notifications from 'Components/Notifications/Notifications.vue';
import adminSignedAsUser from 'Root/mixins/adminSignedAsUser';
import ScreenHolder from 'Components/ScreenHolder/ScreenHolder.vue';
import MobilePanelPopup from 'Components/Popups/MobilePanelPopup.vue';
import CookiesPopup from 'Components/Popups/CookiesPopup.vue';
import { applyPromocode } from 'Root/helpers/api';
import SignInPopup from 'Components/Popups/SignInPopup.vue';
import SignInModel from 'Models/SignInModel';
import { SOCKET_CHAT } from 'Root/plugins/socket/SocketAdapter';
import UserModel from 'Models/UserModel';
import socketMixin from 'Root/mixins/socketMixin';
import chatMixin from 'Root/mixins/chatMixin';
import { addNewMessageEventListeners } from 'Root/helpers/newMessageEventListeners';
import altcraftAnalytics from 'Root/drivers/altcraftAnalytics';
import cookieControllerInstance from 'Root/drivers/ABTestsDriver/cookieController';
import onlineTrackingMixin from 'Root/mixins/onlineTrackingMixin';
import GoogleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import SetPasswordNotification from 'Components/SetPasswordNotification.vue';
import altcraftScriptLoader from 'Root/mixins/altcraftScriptLoader';
import { EU_COUNTRY_CODES } from 'Models/CountryCodes';
import yandexMetrika from 'Root/drivers/yandexMetrika';
import EmailVerificationPopup from 'Components/Popups/EmailVerificationPopup.vue';
import ChangePaymentSystemPopup from 'Components/Popups/ChangePaymentSystemPopup.vue';
// import IntercomApiDriver from './drivers/IntercomApiDriver';

export default {
  name: 'MainApp',

  components: {
    TooltipLayer,
    Notifications,
    ScreenHolder,
    SearchInput,
    SetPasswordNotification,
    CookiesPopup,
  },

  mixins: [
    adminSignedAsUser,
    socketMixin,
    chatMixin,
    onlineTrackingMixin,
    altcraftScriptLoader,
  ],

  asyncData({ store, context }) {
    store.commit('mutateConnectionInfo', context.connectionInfo);
    store.commit('mutateActiveRegion', context.cookies.region);

    const userRequestParams = {};
    const query = context.url.split('?');
    if (query[1]) {
      const user = query[1].match(new RegExp('(?<=_user=)(?:(?!&).)*', 'g'));

      if (user && user[0]) {
        [userRequestParams._user] = user;
      }
    }

    const cookie = Object.entries(context.cookies).map(([key, value]) => `${key}=${value};`).join(' ');

    const userPromise = axios.get(`${AppModel.INTERNAL_URL}/shop/user`, {
      headers: {
        cookie,
        'X-Forwarded-For': context.connectionInfo.userIp,
      },
      params: userRequestParams,
    })
      .then((response) => {
        context.userData = response.data.user;

        const anonymCookie = process.env.COOKIE_ONLINE_ANONYM ? process.env.COOKIE_ONLINE_ANONYM : 'anonym';
        let newSession = response.headers['set-cookie']
          .find((cookieStr) => cookieStr.includes(`${AppModel.SESSION_COOKIE}=`));

        if (newSession) {
          newSession = newSession.match(new RegExp(`(?<=${AppModel.SESSION_COOKIE}=)(?:(?!;).)*`, 'g'));
          if (newSession[0]) {
            context._setCookie[AppModel.SESSION_COOKIE] = {
              value: newSession[0],
              options: {
                maxAge: 86400000,
                httpOnly: true,
              },
            };
          }
        } else {
          context._setCookie._online = {
            value: anonymCookie,
            options: {
              maxAge: 86400000,
              httpOnly: true,
            },
          };
        }

        const online = response.headers['set-cookie']
          .find((cookieStr) => cookieStr.includes('_online='));
        if (online) {
          const isAnonym = online.match(new RegExp('(?<=_online=)(?:(?!;).)*', 'g'));
          if (isAnonym && isAnonym[0] === anonymCookie) {
            context._setCookie._online = {
              value: anonymCookie,
              options: {
                maxAge: 86400000,
                httpOnly: true,
              },
            };
          }
        }

        store.commit('mutateUser', response.data.user);
      });

    const currencyRatePromise = store.dispatch('getCurrencyRate');

    return Promise.all([userPromise, currencyRatePromise]);
  },

  data() {
    return {
      regionId: null,
      showCookieNotification: false,
      showSetPasswordNotification: false,
      vuedalsIsReady: false,
      vuedalsClickType: '',
      isLoaded: false,
    };
  },

  computed: {
    ...mapState(['activeGame', 'user', 'backgrounds', 'usdToCurrencyRatio', 'activeRegion', 'countryCode']),
    ...mapState('products', ['product']),
    ...mapGetters(['userCashback', 'isPlatformApp']),

    throttledWindowResizeHandler() {
      return throttle(() => {
        this.updateUserDeviceInfo();
      }, 150);
    },

    is404() {
      return this.$store.state.httpErrorCode === HttpErrorModel.NOT_FOUND;
    },

    userHasCashback() {
      return this.userCashback && this.userCashback.value_usa > 0;
    },

    isEU() {
      return this.$store.state.activeRegion?.id === 2;
    },

    shouldShowCookieNotification() {
      // SKY-5220: показываем стандартную плашку только для региона USA,
      // для EU - показываем расширенную плашку с выбором настроек
      return !this.isPlatformApp
              && this.showCookieNotification
              && !this.showSetPasswordNotification
              && !this.isEU;
    },

    shouldShowExtendedCookieNotification() {
      // WARNING: Сломает отображения куки попапа на локалке
      return !this.isPlatformApp
              && this.showCookieNotification
              && this.isEU;
    },

    shouldOpenEmailVerificationPopup() {
      return ( // Update resources/assets/js/components/Popups/altcraftPopups/AltcraftContainer.vue
        this.user
              && !this.user.email_verified_at
              && this.user.created_at > +process.env.SHOW_EMAIL_VERIFICATION_POPUP_TIME
              && this.user.role === UserModel.ROLES.CUSTOMER.id
              && !this.isPlatformApp
              && this.$route.name !== 'agreement'
              && !localStorage.getItem(UserModel.EMAIL_VERIFY_LATER_TOKEN_NAME)
      );
    },

    shouldOpenChangePaymentSystemPopup() {
      return this.$route.query?.paid?.toString() === 'false'
        && this.$route.query?.skycoach_order_id && !this.isPlatformApp;
    },

    clientPaidOrdersCount() {
      return this.user?.customer_counters?.paid_orders_count || 0;
    },
  },

  watch: {
    '$route.query': {
      handler(newValue) {
        if (!newValue.signInComponent) {
          return;
        }

        this.openSignInPopup();
      },
    },

    '$route.name': {
      handler(newValue) {
        if (this.shouldOpenChangePaymentSystemPopup) {
          this.openChangePaymentSystemPopup();
          return;
        }

        if (newValue !== 'agreement') {
          if (this.shouldOpenEmailVerificationPopup) {
            this.openEmailVerificationPopup();
          }
        }
      },
    },

    shouldShowExtendedCookieNotification(value) {
      if (typeof window !== 'undefined') {
        if (value) {
          yandexMetrika.invokeEvent('dma_cookie_view');
        }
      }
    },
  },

  async afterAsyncData({ context }) {
    cookieControllerInstance.setSSRCookies(context.cookies);
  },

  async created() {
    if (this.$store.state.currencyRatio) {
      PaymentSystemModel.STRIPE_RADAR_FIX_COMMISION[RegionModel.USA.id] = PaymentSystemModel
        .STRIPE_RADAR_FIX_COMMISION[RegionModel.EU.id] / this.usdToCurrencyRatio[RegionModel.EU.id];
    }

    if (typeof window !== 'undefined') {
      setWindowVisibilityAPI();
      // client-side region from ssr
      this.regionId = this.$store.state.activeRegion?.id;

      if (!this.regionId) {
        // get regionId from localStorage
        this.regionId = +localStorage.getItem(RegionModel.REGION_COOKIE);
      }

      // client-side region from cookies
      if (!this.regionId) {
        this.regionId = this.$cookie.get(RegionModel.REGION_COOKIE);
      }

      this.initUserFromWindow();
      this.savePromocode();

      if (!this.$cookie.get('cookie-policy')) {
        this.showCookieNotification = true;
      }

      this.$watch('user', (newValue) => {
        if (newValue && newValue.role === UserModel.ROLES.CUSTOMER.id) {
          this.initSocketAndChat();
        }
      }, { immediate: true });

      if (!this.$store.state.currencyRatio) {
        this.$store.dispatch('getCurrencyRate').then(() => {
          PaymentSystemModel.STRIPE_RADAR_FIX_COMMISION[RegionModel.USA.id] = PaymentSystemModel
            .STRIPE_RADAR_FIX_COMMISION[RegionModel.EU.id] / this.usdToCurrencyRatio[RegionModel.EU.id];
        });
      }

      if (this.$route.query.ref) {
        localStorage.setItem('referee', this.$route.query.ref);
      }

      if (window?.visibilityAPI?.event) {
        document.addEventListener(window.visibilityAPI.event, () => {
          if (document[window.visibilityAPI.hidden]) {
            this._skGlobal.EVENTS.NATIVE.TAB.DEACTIVATED.dispatch();
          } else {
            this._skGlobal.EVENTS.NATIVE.TAB.ACTIVATED.dispatch();
          }
        });
      }

      if (this.isPlatformApp) {
        GoogleTagAnalytics.updateConsent({
          ad_storage: window?.analytics_consent?.google?.marketing ? 'granted' : 'denied',
          ad_user_data: window?.analytics_consent?.google?.marketing ? 'granted' : 'denied',
          ad_personalization: window?.analytics_consent?.google?.marketing ? 'granted' : 'denied',
          analytics_storage: window?.analytics_consent?.google?.analytics ? 'granted' : 'denied',
          functionality_storage: 'granted',
          security_storage: 'granted',
        });
      } else if ('localStorage' in window) {
        const consent = window.localStorage.getItem('cookies-consent');

        // Показываем плашку согласия на использование куков повторно,
        // т.к проебався с сохранением настроек согласия
        if (
          !consent
          && EU_COUNTRY_CODES.includes(this.countryCode)
          && this.$cookie.get('cookie-policy')
        ) {
          this.showCookieNotification = true;
        } else if (consent) {
          GoogleTagAnalytics.updateConsent(JSON.parse(consent));
        }
      }

      this.showSetPasswordNotification = !this.isPlatformApp
        && this.user
        && !this.user.has_password
        && this.user.role === UserModel.ROLES.CUSTOMER.id
        && this.clientPaidOrdersCount < 1
        && 'localStorage' in window
        && localStorage.getItem(UserModel.SET_PASSWORD_LATER_TOKEN_NAME);
    } else if (this.$ssrContext.cookies['cookie-policy'] !== 'true') {
      this.showCookieNotification = true;
    }

    if (!this.regionId) {
      // server-side region from cookies or fallback region
      this.regionId = this.$ssrContext.cookies.region || RegionModel.EU.id;
    }

    if (!this.$store.state.activeRegion || this.regionId !== this.$store.state.activeRegion.id) {
      this.mutateActiveRegion(this.regionId);
    }

    if (this.$store.state.user) {
      GoogleTagAnalytics.setUserData({ email: this.$store.state.user.email });
      GoogleTagAnalytics.fireUserId(this.$store.state.user.id);
      altcraftAnalytics.firePageView(this.$store.state.user);
    }
  },

  mounted() {
    this.isLoaded = true;
    const isHover = window.matchMedia('(hover: hover)').matches;
    const isCoarse = window.matchMedia('(pointer:coarse)').matches;
    const isSafari = navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') <= -1;
    this.updateUserDeviceInfo({
      isHover,
      isCoarse,
      hasTouch,
      isSafari,
    });

    window.addEventListener('resize', this.throttledWindowResizeHandler);

    const ua = navigator.userAgent;
    if (/android/i.test(ua)) {
      this.mutateUserDevice({
        platformFromUA: 'android',
      });
    } else if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      this.mutateUserDevice({
        platformFromUA: 'ios',
      });
    }

    this.$cookie.set(RegionModel.REGION_COOKIE, this.regionId, { expires: '5Y' });

    this.saveInitialURL();

    // this.initIntercom();

    import('vuedals')
      .then(({ Component }) => {
        this.$options.components.vuedal = Component;
        this.vuedalsIsReady = true;
        setTimeout(async () => {
          this.$vuedals.isReady = true;
          this.$refs.vuedals.handleBackdropClick = () => {
            if (this.vuedalsClickType !== 'mousedown') {
              return;
            }
            Component.methods.handleBackdropClick.apply(this.$refs.vuedals);
          };
          window.dispatchEvent(new Event('vuedals-ready'));

          this.openPromocodePopup();

          if (this.$route.query?.email_verify_token) {
            const response = await this.$axios.get(`${AppModel.API_URI}/user/verify_email`, {
              params: {
                email_verify_token: this.$route.query.email_verify_token,
              },
            });

            if (response.status === 200) {
              await this.getUser();
              yandexMetrika.invokeEvent('email_verify_successful');
            }
          }

          if (this.shouldOpenChangePaymentSystemPopup) {
            this.openChangePaymentSystemPopup();
            return;
          }

          if (this.shouldOpenEmailVerificationPopup) {
            this.openEmailVerificationPopup();
          } else {
            this.openSignInPopup();
          }
        }, 0);
      });

    const basicOpen = this.$vuedals.open;
    this.$vuedals.open = (...params) => {
      document.body.classList
        .toggle('vuedal-scroll-padding', document.body.scrollHeight > window.innerHeight);
      basicOpen(...params);
    };

    this.$vuedals.setClass = (customClass, state = true) => {
      const classArray = Array.isArray(customClass)
        ? customClass
        : [customClass];
      if (state) {
        this.$refs.vuedals.$el.classList.add(...classArray);
      } else {
        this.$refs.vuedals.$el.classList.remove(...classArray);
      }
    };

    this.$vuedals.openWithClass = (classes, options) => {
      this.$vuedals.setClass(classes);
      this.$vuedals.open({
        ...options,

        title: classes.join('.'),

        onDismiss: () => {
          const classesToRemove = classes
            .filter((c) => this.$refs.vuedals.vuedals.filter((v) => v.title.split('.').includes(c)).length <= 1);

          this.$vuedals.setClass(classesToRemove, false);

          if (typeof options.onDismiss === 'function') {
            options.onDismiss();
          }
        },

        onClose: (data) => {
          const classesToRemove = classes
            .filter((c) => this.$refs.vuedals.vuedals.filter((v) => v.title.split('.').includes(c)).length <= 1);

          this.$vuedals.setClass(classesToRemove, false);

          if (typeof options.onClose === 'function') {
            options.onClose(data);
          }
        },
      });
    };

    this.$vuedals.openMobilePanel = (options) => {
      this.$vuedals.openWithClass(options.class ? ['blur', options.class] : ['blur'], {
        component: MobilePanelPopup,
        name: `mobile-panel-popup ${options.name || ''}`,
        onClose: options.onClose,
        onDismiss: options.onDismiss,
        closeOnBackdrop: options.closeOnBackdrop !== undefined ? options.closeOnBackdrop : true,
        props: {
          ...options,
        },
      });
    };

    setWindowFPS();

    setTimeout(() => this.loadAltcraftPush(), 1000);
  },

  methods: {
    ...mapMutations(['mutateActiveRegion', 'mutateUser', 'mutateUserDevice']),
    ...mapActions(['getUser']),

    closeVuedal() {
      this.vuedalsClickType = 'mousedown';
      this.$refs.vuedals.handleBackdropClick.apply(this.$refs.vuedals);
    },

    acceptCookie() {
      this.$cookie.set('cookie-policy', true, { expires: '5Y' });
      this.showCookieNotification = false;
    },

    closeCookie() {
      this.showCookieNotification = false;
    },

    savePromocode() {
      const promocode = this.$route.query[POPUP_PROMOCODE_APPLIED.getParamName];

      if (promocode) {
        this.$cookie.set(POPUP_PROMOCODE_APPLIED.cookieName, promocode, { expires: 7 });
      }
    },

    updateUserDeviceInfo(additionalOptions) {
      const { innerWidth, innerHeight } = window;
      this.mutateUserDevice({
        ...additionalOptions,
        innerWidth,
        innerHeight,
      });
    },

    initUserFromWindow() {
      if (window.user) {
        this.mutateUser(window.user);
        const tempUserNode = document.querySelector('#script-user-temp');

        if (tempUserNode) {
          tempUserNode.remove();
        }
      }
    },

    // initIntercom() {
    //   window.addEventListener('load', () => {
    //     IntercomApiDriver.init(this.user);
    //   });
    // },

    openPromocodePopup() {
      const promocode = this.$route.query[POPUP_PROMOCODE_APPLIED.getParamName];
      let promoInfo = null;
      let applyPromocodePromise = null;

      if (!promocode || this.userHasCashback) {
        return;
      }

      if (this.$route.name.startsWith('game__') || this.$route.name === 'root') {
        const params = {
          promocode,
          regionId: this.regionId,
          userEmail: this.user?.email || null,
        };

        applyPromocodePromise = applyPromocode(params)
          .then(({ data }) => {
            if (!data.promocode || !data.is_available) {
              return;
            }

            promoInfo = { ...data.promocode };
          })
          .catch((error) => {
            console.log(error);
          });
      }

      import('Components/Popups/PromocodeAppliedPopup.vue')
        .then(async (module) => {
          if (applyPromocodePromise) {
            await applyPromocodePromise;
          }

          if (promoInfo?.game_ids !== null && !promoInfo?.game_ids.includes(this.activeGame.id)) {
            promoInfo = null;
          }

          this.$vuedals.openMobilePanel({
            name: POPUP_PROMOCODE_APPLIED.name,
            component: module.default,
            data: { promoInfo },
          });
        });
    },

    openSignInPopup() {
      if (this.$route.query?.email && Array.isArray(this.$route.query?.email)) {
        this.$route.query.email = this.$route.query?.email[0];
      }

      /* eslint-disable camelcase */
      const {
        email,
        skycoach_token: token,
        redirectPathAfterSignIn,
        signInComponent,
      } = this.$route.query;

      const tokenFromLocalStorage = localStorage.getItem(UserModel.SET_PASSWORD_TOKEN_NAME);
      const setPasswordLater = this.clientPaidOrdersCount < 1
        ? localStorage.getItem(UserModel.SET_PASSWORD_LATER_TOKEN_NAME)
        : false;

      const signinEmail = new URLSearchParams(redirectPathAfterSignIn).get('email');
      // todo: SKY-2969: fix it
      const userWithoutPassword = this.user
        && !this.user.has_password
        && this.user.role === UserModel.ROLES.CUSTOMER.id;
      const shouldOpenNewPasswordView = (email && token) || (userWithoutPassword && !setPasswordLater);

      if (shouldOpenNewPasswordView || redirectPathAfterSignIn || signInComponent) {
        const initialView = token || tokenFromLocalStorage
          ? (signInComponent || SignInModel.POPUP_VIEWS.NEW_PASSWORD)
          : (signInComponent || SignInModel.POPUP_VIEWS.SIGN_IN);
        const closeOnlyOnSubmit = (!!token || !!tokenFromLocalStorage)
          && this.clientPaidOrdersCount > 0;

        this.$vuedals.openMobilePanel({
          class: 'sign-in-popup',
          component: SignInPopup,
          closeOnBackdrop: !closeOnlyOnSubmit,
          data: {
            initialView,
            isClosable: !closeOnlyOnSubmit,
            email: email || signinEmail || this.user?.email,
            token: token || tokenFromLocalStorage,
            redirectPathAfterSignIn,
          },
          onDismiss: () => {
            if (initialView === SignInModel.POPUP_VIEWS.NEW_PASSWORD) {
              localStorage.setItem(UserModel.SET_PASSWORD_LATER_TOKEN_NAME, 'true');
              this.$nextTick(() => {
                this.showSetPasswordNotification = !this.user.has_password;
              });
            }
          },
        });
      }
      /* eslint-enable */
    },

    openChangePaymentSystemPopup() {
      this.$vuedals.openMobilePanel({
        class: 'change-payment-system-popup',
        component: ChangePaymentSystemPopup,
        closeOnBackdrop: true,
        data: {
          isClosable: true,
          orderId: this.$route.query.skycoach_order_id,
        },
      });
    },

    openEmailVerificationPopup() {
      this.$vuedals.openMobilePanel({
        class: 'email-verification-popup',
        component: EmailVerificationPopup,
        closeOnBackdrop: false,
        data: {
          isClosable: false,
          email: this.user?.email,
          isMarketingEmailsAllowed: this.user?.is_marketing_emails_allowed,
          hasPassword: this.user?.has_password,
        },
        onClose: () => {
          this.openSignInPopup();
        },
      });
    },

    openSetPasswordPopup() {
      if (!this.user || this.user?.has_password) {
        return;
      }

      this.$store.dispatch('getUser').then(() => {
        this.$vuedals.openMobilePanel({
          class: 'sign-in-popup',
          component: SignInPopup,
          closeOnBackdrop: true,
          data: {
            initialView: SignInModel.POPUP_VIEWS.NEW_PASSWORD,
            isClosable: false,
            email: this.user.email,
            token: localStorage.getItem(UserModel.SET_PASSWORD_TOKEN_NAME),
          },
          onDismiss: () => {
            this.$nextTick(() => {
              this.showSetPasswordNotification = !this.user.has_password;
            });
          },
        });
      });
    },

    initSocketAndChat() {
      this._initSocketAdapter();
      this.$socket.connectToProvider(SOCKET_CHAT);
      this._initClientChat();
      addNewMessageEventListeners(this);
    },

    saveInitialURL() {
      const initialURLPage = sessionStorage.getItem('initialURLPage');

      if (this.isPlatformApp || initialURLPage) {
        return;
      }

      sessionStorage.setItem('initialURLPage', window.location.href);
    },
  },
};
</script>

<style lang="scss" scoped>
  .root {
    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
</style>
